import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { Home, Users, CalendarCheck, Building, Building2, Mail, Plug, BarChart3, UserCog, FileText, LogOut, ShieldCheck, Send } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import ThemeToggle, { useTheme } from "../../components/ThemeToggle";
import { api } from "../../lib/api";

const NAV = [
  { to: "/dashboard", label: "Leads", icon: Users, end: true },
  { to: "/dashboard/viewings", label: "Viewings", icon: CalendarCheck },
  { to: "/dashboard/inventory", label: "Inventory", icon: Building },
  { to: "/dashboard/companies", label: "Companies", icon: Building2 },
  { to: "/dashboard/outreach", label: "Outreach", icon: Mail },
  { to: "/dashboard/email", label: "Email Centre", icon: Send },
  { to: "/dashboard/data-requests", label: "Data Requests", icon: ShieldCheck },
  { to: "/dashboard/ingestion", label: "Ingestion", icon: Plug },
  { to: "/dashboard/analytics", label: "Analytics", icon: BarChart3 },
  { to: "/dashboard/users", label: "Users", icon: UserCog, admin: true },
  { to: "/dashboard/content", label: "Content", icon: FileText, admin: true },
];

export default function DashLayout() {
  const { user, logout } = useAuth();
  const [dark, setDark] = useTheme();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);

  useEffect(() => { api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {}); }, []);

  const doLogout = async () => { await logout(); navigate("/staff"); };

  return (
    <div className="min-h-screen flex bg-background">
      <aside className="w-56 shrink-0 border-r border-border bg-card hidden md:flex flex-col">
        <div className="p-5 border-b border-border">
          <NavLink to="/" className="flex items-center gap-2">
            <span className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center"><Home className="w-4 h-4 text-primary-foreground" /></span>
            <span className="font-heading font-bold">Tendly</span>
            <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">CRM</span>
          </NavLink>
        </div>
        <nav className="flex-1 p-3 space-y-1" aria-label="Dashboard">
          {NAV.filter((n) => !n.admin || user?.role === "admin").map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} data-testid={`dash-nav-${n.label.toLowerCase()}`}
              className={({ isActive }) => `flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors ${isActive ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary hover:text-foreground"}`}>
              <n.icon className="w-4 h-4" /> {n.label}
              {n.label === "Leads" && stats?.leads_new > 0 && <span className="ml-auto rounded-full bg-accent text-accent-foreground text-[10px] font-bold px-1.5 py-0.5">{stats.leads_new}</span>}
              {n.label === "Outreach" && stats?.outreach_drafts > 0 && <span className="ml-auto rounded-full bg-accent text-accent-foreground text-[10px] font-bold px-1.5 py-0.5">{stats.outreach_drafts}</span>}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-border flex items-center justify-between gap-2">
          <div className="min-w-0">
            <p className="text-sm font-semibold truncate">{user?.name}</p>
            <p className="text-xs text-muted-foreground capitalize">{user?.role}</p>
          </div>
          <ThemeToggle dark={dark} onToggle={setDark} />
        </div>
      </aside>

      <div className="flex-1 min-w-0 flex flex-col">
        <header className="md:hidden sticky top-0 z-40 border-b border-border bg-card/90 backdrop-blur-xl px-4 py-3 flex items-center justify-between">
          <NavLink to="/" className="flex items-center gap-2"><Home className="w-5 h-5 text-primary" /><span className="font-heading font-bold">Tendly CRM</span></NavLink>
          <div className="flex items-center gap-2">
            <ThemeToggle dark={dark} onToggle={setDark} />
            <button data-testid="dash-logout-mobile" onClick={doLogout} aria-label="Sign out" className="p-2 text-muted-foreground"><LogOut className="w-4 h-4" /></button>
          </div>
        </header>
        <nav className="md:hidden flex gap-1 overflow-x-auto px-3 py-2 border-b border-border bg-card" aria-label="Dashboard mobile">
          {NAV.filter((n) => !n.admin || user?.role === "admin").map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end}
              className={({ isActive }) => `shrink-0 rounded-full px-3 py-1.5 text-xs font-medium ${isActive ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="hidden md:flex items-center justify-end gap-3 border-b border-border bg-card px-6 py-3">
          <span className="text-sm text-muted-foreground">{user?.email}</span>
          <button data-testid="dash-logout" onClick={doLogout} className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground"><LogOut className="w-4 h-4" /> Sign out</button>
        </div>
        <main className="flex-1 p-4 md:p-8 max-w-[1400px] w-full mx-auto">
          <Outlet context={{ refreshStats: () => api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {}) }} />
        </main>
      </div>
    </div>
  );
}
